import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { MessagesSquare, ArrowBigUp, MessageCircle, Plus, Send, Loader2 } from "lucide-react";

const TAGS = ["All", "Engineering", "Medical", "Study Abroad", "Design", "General"];

export default function CommunityPage() {
  const [posts, setPosts] = useState([]);
  const [tag, setTag] = useState("All");
  const [active, setActive] = useState(null);

  const load = () => api.get("/forum", { params: tag !== "All" ? { tag } : {} }).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tag]);

  const upvote = async (id) => {
    try {
      const { data } = await api.post(`/forum/${id}/upvote`);
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, upvotes: data.upvotes } : p)));
    } catch { toast.error("Login required"); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 py-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <Header icon={MessagesSquare} eyebrow="Community"
          title="Student discussion forum" desc="Ask questions, share wisdom, help peers." />
        <NewPostDialog onCreated={load} />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TAGS.map((t) => (
          <button key={t} onClick={() => setTag(t)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
              tag === t ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"
            }`}>{t}</button>
        ))}
      </div>

      <div className="space-y-4">
        {posts.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            data-testid="forum-post-card" className="rounded-2xl border border-white/8 bg-[#131C2E] p-5 flex gap-4">
            <button data-testid="forum-upvote-btn" onClick={() => upvote(p.id)}
              className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-cyan-400 transition-colors">
              <ArrowBigUp className="h-6 w-6" />
              <span className="font-mono-data text-sm font-bold">{p.upvotes}</span>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="rounded-full bg-indigo-400/10 px-2.5 py-0.5 text-[10px] font-semibold text-indigo-300">{p.tag}</span>
                <span className="text-xs text-slate-500">by {p.author}</span>
              </div>
              <h3 className="font-display font-semibold text-white">{p.title}</h3>
              <p className="mt-1 text-sm text-slate-400 line-clamp-2">{p.body}</p>
              <button onClick={() => setActive(p)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-400">
                <MessageCircle className="h-3.5 w-3.5" /> {p.reply_count} replies · View thread
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <ThreadDialog post={active} onClose={() => setActive(null)} onReplied={load} />
    </div>
  );
}

function NewPostDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tag: "General" });
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (!form.title || !form.body) { toast.info("Add a title & details."); return; }
    setLoading(true);
    try {
      await api.post("/forum", form);
      toast.success("Posted! +20 XP");
      setOpen(false);
      setForm({ title: "", body: "", tag: "General" });
      onCreated();
    } catch { toast.error("Failed to post"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="forum-new-post-btn"
          className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] transition-transform hover:scale-105">
          <Plus className="h-4 w-4" /> New Post
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display">Ask the community</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Your question title"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          <textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Add details..."
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          <div className="flex flex-wrap gap-2">
            {TAGS.filter((t) => t !== "All").map((t) => (
              <button key={t} onClick={() => setForm({ ...form, tag: t })}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${form.tag === t ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400"}`}>{t}</button>
            ))}
          </div>
          <button onClick={create} disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Post question</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ThreadDialog({ post, onClose, onReplied }) {
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (post) api.get(`/forum/${post.id}`).then((r) => setDetail(r.data));
    else setDetail(null);
  }, [post]);

  const send = async () => {
    if (!reply.trim()) return;
    setLoading(true);
    try {
      await api.post(`/forum/${post.id}/reply`, { body: reply });
      const { data } = await api.get(`/forum/${post.id}`);
      setDetail(data);
      setReply("");
      onReplied();
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!post} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white max-w-lg">
        {detail && (
          <>
            <DialogHeader><DialogTitle className="font-display">{detail.title}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-400">{detail.body}</p>
            <div className="max-h-56 overflow-y-auto space-y-3 border-t border-white/8 pt-3">
              {(detail.replies || []).length === 0 && <p className="text-xs text-slate-500">No replies yet. Be the first!</p>}
              {(detail.replies || []).map((r) => (
                <div key={r.id} className="rounded-xl bg-white/[0.02] p-3">
                  <p className="text-xs font-semibold text-cyan-300">{r.author}</p>
                  <p className="text-sm text-slate-300 mt-0.5">{r.body}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply..."
                onKeyDown={(e) => e.key === "Enter" && send()}
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
              <button onClick={send} disabled={loading} className="rounded-xl bg-cyan-400 px-4 text-[#041016] disabled:opacity-50">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
